import { isBoutiqueSide } from "@/features/invoices/invoice-utils";
import { convertOrderStatusToNew } from "@/shared/utils";
import { OrderHistoryGetDetailResponse } from "@/features/my-order/features/order-history/types";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import {
  isDeductibleCredit,
  isUnoFeeDeductibleCredit,
} from "@/features/invoices/credit-invoices/credit-invoice.type";
import { RemittanceStatus } from "@/features/invoices/invoice.type";
import { Transportation } from "@/features/transportations/transportation.type";
import { Price } from "@/features/types";

export const getOrderHistoryRenderStatus = (
  data?: OrderHistoryGetDetailResponse
) => {
  if (!data) return {};
  const status = data.status;

  return {
    MODIFICATION:
      convertOrderStatusToNew(status) === "ORDER_SUBMITTED" ||
      convertOrderStatusToNew(status) === "BUYER_MODIFICATION_REQUIRED" ||
      convertOrderStatusToNew(status) === "RESUBMIT_COMPLETED",
    CONFIRMED:
      convertOrderStatusToNew(status) === "BUYER_CONFIRM_REQUIRED" ||
      convertOrderStatusToNew(status) === "ORDER_CONFIRMED" ||
      convertOrderStatusToNew(status) === "TRANSPORTATION_ING" ||
      convertOrderStatusToNew(status) === "TRANSPORTATION_COMPLETED" ||
      convertOrderStatusToNew(status) === "COMPLETED",
    ORDER_SHEET: data?.line_sheet_set.status === "PARSING_COMPLETED",
    MODIFICATION_ONLY:
      convertOrderStatusToNew(status) === "BUYER_MODIFICATION_REQUIRED",
  };
};

// 서버(파이썬 round)와 같은 반올림 — .5 는 짝수 쪽으로 보낸다. 실제 송금 원화 금액을 서버 송금 요청 금액과 맞춘다.
export const extractInvoiceFromOrderHistory = (data?: ProformaInvoice) => {
  // 인보이스 금액 통화. 원화 프로포마(국내 부티크 오더 거래명세서 등)는 바이어가 부티크에 원화로
  // 바로 보낸다 — 환전·해외 송금이 없어 인보이스 금액이 곧 낼 금액이다.
  const currency = data?.totalAmount?.currency ?? "EUR";
  const isKrwInvoice = currency === "KRW";
  // 바이어 유로 송금 프로포마 — 바이어가 에이전시에 유로로 낸다(원화 환산·은행 수수료·환율 차액 없음)
  const isBuyerToAgencyEur = !isKrwInvoice && !!data?.isBuyerToAgencyEur;
  // 바이어가 낼(낸) 상품 대금의 통화
  const collectedCurrency: "KRW" | "EUR" = isBuyerToAgencyEur ? "EUR" : "KRW";
  // 바이어가 내는 상품 대금 송금 요청 — 원화 프로포마는 바이어 → 부티크, 그 외는 바이어 → 에이전시
  const buyerProductPayment = data?.payments?.find(
    (payment) =>
      payment.type === "PRODUCT" &&
      (isKrwInvoice
        ? payment.sender.type === "BUYER" &&
          isBoutiqueSide(payment.receiver.type)
        : payment.receiver.type === "AGENCY")
  );
  const agencyProductPayment = data?.payments?.find(
    (payment) =>
      payment.type === "PRODUCT" && isBoutiqueSide(payment.receiver.type)
  );
  const agencyAdjustmentPayment = data?.payments?.find(
    (payment) => payment.type === "ADJUSTMENT"
  );

  // 인보이스 총액은 인보이스 금액 그대로다(3자무역 수수료를 곱하지 않는다, 2026-10-30)
  const withCrossTradeFee = (value: number) => Math.round(value * 100) / 100;
  const invoiceAmount = withCrossTradeFee(data?.totalAmount?.value ?? 0);
  const usedDeposit = data?.usedDeposit?.value ?? 0;
  const finalInvoiceAmount = invoiceAmount - usedDeposit;
  // 바이어 카드에는 바이어 소유 크레딧만 보인다 — 에이전시 소유 크레딧은 에이전시가 따로 정산하는 몫이라
  // 송금 필요 금액에서도 빠지지 않으므로, 서버의 usedCredit(전체 합) 대신 연결 크레딧에서 걸러 합산한다.
  const usedCredit = withCrossTradeFee(
    data?.usedCreditInvoices
      ? data.usedCreditInvoices
          .filter(isDeductibleCredit)
          .reduce((sum, credit) => sum + (credit.amount?.value ?? 0), 0)
      : data?.usedCredit?.value ?? 0
  );
  const totalEuroAmount = data?.totalRequiredAmount?.value ?? 0;
  const exchangeRate = data?.exchangeRate ?? 0;
  const actualExchangeRate = data?.actualExchangeRate ?? 0;
  const totalKrwAmount = totalEuroAmount * exchangeRate;
  const actualTotalKrwAmount = totalEuroAmount * actualExchangeRate;
  const bankFee = data?.bankFee?.value ?? 0;
  const actualBankFee = data?.actualBankFee?.value ?? 0;
  // 이름은 Krw 지만 통화는 collectedCurrency 를 따른다(유로 송금 프로포마면 유로)
  const totalCollectedKrwAmount =
    isKrwInvoice || isBuyerToAgencyEur
      ? buyerProductPayment?.amount.value ?? totalEuroAmount
      : buyerProductPayment?.amount.value ?? 0;
  const actualTotalCollectedKrwAmount =
    isKrwInvoice || isBuyerToAgencyEur
      ? totalEuroAmount
      : // 서버(_buyer_share_krw)와 같이 원 미만 절사 — 은행 외환전표가 그렇게 출금한다(2026-10-29 프로포마 1073)
        Math.floor((actualTotalKrwAmount ?? 0) + actualBankFee);
  const adjustmentAmount =
    agencyAdjustmentPayment?.receiver.type === "BUYER"
      ? (agencyAdjustmentPayment?.amount?.value ?? 0) * -1
      : agencyAdjustmentPayment?.amount?.value;

  const isRemittedToBoutique = agencyProductPayment?.isRemitted ?? false;
  const isRemittedToAgency = buyerProductPayment?.isRemitted ?? false;
  const isRemittedAdjustment = agencyAdjustmentPayment?.isRemitted ?? false;

  const showFinalInvoice = data?.usedDeposit?.value !== 0 || usedCredit !== 0;
  const showAdjustment = !!adjustmentAmount && adjustmentAmount !== 0;
  const issuedOn = data?.issuedOn ?? "";
  const remittedOn = buyerProductPayment?.remittedOn ?? "";
  const adjustmentRemittedOn = agencyAdjustmentPayment?.remittedOn ?? "";

  const proformaFileId = data?.file?.id;

  const getAdjustmentStatus = () => {
    if (!adjustmentAmount || adjustmentAmount === 0) {
      throw new Error("adjustmentAmount must not be 0");
    }

    if (isRemittedAdjustment) {
      return adjustmentAmount > 0 ? "REMITTED" : "RETURNED";
    } else {
      return adjustmentAmount > 0 ? "REMITTANCE_REQUIRED" : "RETURN_REQUIRED";
    }
  };

  const adjustmentResolveRequired = !!(
    adjustmentAmount &&
    adjustmentAmount !== 0 &&
    (getAdjustmentStatus() === "REMITTANCE_REQUIRED" ||
      getAdjustmentStatus() === "RETURN_REQUIRED")
  );

  return {
    data,
    currency,
    isKrwInvoice,
    isBuyerToAgencyEur,
    collectedCurrency,
    invoiceAmount,
    usedDeposit,
    finalInvoiceAmount,
    usedCredit,
    totalEuroAmount,
    exchangeRate,
    actualExchangeRate,
    isRemittedToBoutique,
    totalKrwAmount,
    actualTotalKrwAmount,
    bankFee,
    actualBankFee,
    totalCollectedKrwAmount,
    actualTotalCollectedKrwAmount,
    isRemittedToAgency,
    isRemittedAdjustment,
    adjustmentAmount,
    showFinalInvoice,
    issuedOn,
    remittedOn,
    adjustmentRemittedOn,
    showAdjustment,
    proformaFileId,
    adjustmentResolveRequired,
    getAdjustmentStatus,
  };
};

export const extractTransportationFromOrderHistory = (
  data?: Transportation
) => {
  const transportationPayment = data?.transportationPayment;
  const fundRequestAmount = transportationPayment?.amount
    ? typeof transportationPayment?.amount === "number"
      ? transportationPayment?.amount
      : (transportationPayment?.amount as Price)?.value
    : undefined ?? 0;
  const status = data?.status;

  const issuedOn = data?.transportationPayment?.issuedOn;
  const remittedOn = data?.transportationPayment?.remittedOn;

  const isRemitted = data?.transportationPayment?.isRemitted;
  const name = data?.name ?? "";

  const receiverName = data?.shippedToRecipientName;
  const receiverContact = data?.shippedToContact;
  const receiverAddress = data?.shippedToAddress;

  const fundRequestFileId = data?.transportationPayment?.file?.id;

  const flightNumber = data?.flightNumber;
  const mAwbNumber = data?.mawbNumber;
  const houseBlNumber = data?.houseBlNumbers;
  const domesticTrackingNumber = data?.domesticTrackingNumber;
  const domesticCarrierName = data?.domesticCarrierName;

  return {
    data,
    fundRequestAmount,
    status,
    issuedOn,
    remittedOn,
    isRemitted,
    name,
    receiverName,
    receiverContact,
    receiverAddress,
    fundRequestFileId,
    transportationPayment,
    flightNumber,
    mAwbNumber,
    houseBlNumber,
    domesticTrackingNumber,
    domesticCarrierName,
  };
};

export const extractUnoFeeFromOrderHistory = (data?: ProformaInvoice) => {
  const unoFeeAmount = data?.unoFeeAmount?.value;
  const unoFeeSettlementMonth = data?.unoFeeSettlementMonth ?? "";
  const unoFeePayment = data?.payments?.find(
    (payment) => payment.type === "UNO_FEE"
  );

  const status =
    unoFeePayment?.status === "COMPLETED"
      ? "REMITTED"
      : ("REMITTANCE_REQUIRED" as RemittanceStatus);
  const isRemitted = !!unoFeePayment?.isRemitted;
  const unoFeeRate = (data?.unoFeeRate ?? 0) * 100;

  // 수수료 기준 금액 = 바이어 부담액(인보이스 금액 − 바이어 소유 사용 크레딧 − 사용 디파짓)
  //                  − "수수료 계산 시 차감" 으로 등록된 바이어 소유 하자(FIXING) 적립 크레딧.
  // 백엔드 _uno_fee_amount 와 같은 식. 에이전시 소유 크레딧은 어느 쪽에서도 빼지 않는다(isDeductibleCredit).
  const { currency, invoiceAmount, usedCredit, usedDeposit } =
    extractInvoiceFromOrderHistory(data);
  const earnedCredit = (data?.earnedCreditInvoices ?? [])
    .filter(
      (credit) => isUnoFeeDeductibleCredit(credit) && isDeductibleCredit(credit)
    )
    .reduce((sum, credit) => sum + (credit.amount?.value ?? 0), 0);
  // 바이어 부담액은 서버 값(total_required_amount)을 우선 쓴다 — 수수료가 실제로 그 값으로 계산됐다.
  const buyerRequiredAmount =
    data?.totalRequiredAmount?.value ??
    invoiceAmount - usedCredit - usedDeposit;
  const unoFeeBaseAmount = Math.max(buyerRequiredAmount - earnedCredit, 0);
  const hasUnoFeeDeduction =
    usedCredit !== 0 || usedDeposit !== 0 || earnedCredit !== 0;

  return {
    unoFeeAmount,
    unoFeeSettlementMonth,
    status,
    isRemitted,
    unoFeeRate,
    currency,
    invoiceAmount,
    usedCredit,
    usedDeposit,
    earnedCredit,
    unoFeeBaseAmount,
    hasUnoFeeDeduction,
  };
};
